/**
 * Project Types for TrafficDesigner
 * 
 * A project stores the complete state of a traffic control plan.
 */

import type { ArrowDescriptor } from './arrows';
import type { DelineatorLine } from './delineators';
import type { PageAnnotation, AnnotationTemplate } from './annotations';

/**
 * Sign placement data stored in project
 */
export interface StoredSign {
  id: string;
  lat: number;
  lng: number;
  rotation: number;
  scale: number;
  sign: {
    code_sa: string;
    svg: string;
    description?: string;
    category?: string;
  };
  calloutOffset?: { x: number; y: number };
  edgeMetadata?: {
    roadSegmentId: string;
    side: string;
    edgeAngle: number;
    perpendicularOffset: number;
  };
  signLineId?: string;
}

/**
 * Workzone polygon data
 */
export interface StoredWorkzone {
  id: string;
  polygon: Array<{ lat: number; lng: number }>;
  location?: { lat: number; lng: number };
  address?: string;
  name?: string;
  city?: string;
  state?: string;
  country?: string;
}

/**
 * Print area / bounding box
 */
export interface StoredPrintArea {
  bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  screenBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

/**
 * Map state
 */
export interface StoredMapState {
  center: { lat: number; lng: number };
  zoom: number;
  bearing?: number;
  pitch?: number;
}

/**
 * Worksite location - the address the user searched for
 */
export interface WorksiteLocation {
  lat: number;
  lng: number;
  address?: string;
  name?: string;
  city?: string;
  state?: string;
  country?: string;
}

/**
 * Complete project data stored in Firebase
 */
export interface ProjectData {
  // Map state
  mapState?: StoredMapState;
  
  // Worksite location (searched address)
  worksiteLocation?: WorksiteLocation;
  
  // Elements
  signs: StoredSign[];
  workzones: StoredWorkzone[];
  arrows: ArrowDescriptor[];
  delineatorLines: DelineatorLine[];
  
  // Print settings
  printArea?: StoredPrintArea;
  paperSize: 'A4' | 'A3' | 'A2' | 'A1' | 'A0';
  orientation: 'portrait' | 'landscape';
  mapFadeOpacity: number;
  
  // Annotations (for the annotation editor)
  annotations: PageAnnotation[];
  
  // Title box data (legacy support)
  titleBoxData?: {
    useTitleBox: boolean;
    exportTitle?: string;
    tcpNumber: string;
    client: string;
    roadType: string;
    contact: string;
    phone: string;
    location: string;
    council: string;
    startDate: string;
    finishDate: string;
    startTime: string;
    finishTime: string;
    author: string;
    createdOn: string;
    modifiedBy: string;
    modifiedOn: string;
    state: string;
    lengthOfZone: string;
    roadSpeed: string;
    logos: {
      left: string | null;
      center: string | null;
      right: string | null;
    };
    customSpacingRequirements?: Record<string, string>;
    showLogoLeft?: boolean;
    showLogoCenter?: boolean;
    showLogoRight?: boolean;
    showMetadataSection?: boolean;
    showSpacingTable?: boolean;
  };
}

/**
 * Project metadata (returned from list endpoint)
 */
export interface ProjectListItem {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
  thumbnail?: string;
  sign_count: number;
  has_print_area: boolean;
}

/**
 * Full project with data (returned from get endpoint)
 */
export interface Project {
  id: string;
  user_id: string;
  name: string;
  data: ProjectData;
  thumbnail?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Create project request
 */
export interface CreateProjectRequest {
  name: string;
  data: ProjectData;
  thumbnail?: string;
}

/**
 * Update project request
 */
export interface UpdateProjectRequest {
  name?: string;
  data?: ProjectData;
  thumbnail?: string;
}

/**
 * Default empty project data
 */
export const DEFAULT_PROJECT_DATA: ProjectData = {
  signs: [],
  workzones: [],
  arrows: [],
  delineatorLines: [],
  paperSize: 'A3',
  orientation: 'landscape',
  mapFadeOpacity: 0,
  annotations: [],
  worksiteLocation: undefined,
};

/**
 * Auto-save status
 */
export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Project context state
 */
export interface ProjectState {
  currentProjectId: string | null;
  currentProjectName: string;
  isDirty: boolean;  // Has unsaved changes
  lastSavedAt: Date | null;
  autoSaveStatus: AutoSaveStatus;
}
